import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { userApi } from '../utils/api';
import toast from 'react-hot-toast';
import { useLanguageStore } from '../stores/languageStore';

const ResetPassword: React.FC = () => {
  const { language } = useLanguageStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(language === 'en' ? 'Invalid verification link' : '无效的验证链接');
      return;
    }

    if (!password.trim() || !confirmPassword.trim()) {
      toast.error(language === 'en' ? 'Please fill in all required fields' : '请填写所有必填项');
      return;
    }

    if (password !== confirmPassword) {
      toast.error(language === 'en' ? 'Passwords do not match' : '两次输入的密码不一致');
      return;
    }

    if (password.length < 6) {
      toast.error(language === 'en' ? 'Password must be at least 6 characters' : '密码长度至少6位');
      return;
    }

    try {
      setLoading(true);
      const response = await userApi.resetPassword(token, password);
      if (response.success) {
        toast.success(language === 'en' ? 'Password reset successfully' : '密码重置成功');
        navigate('/login');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || (language === 'en' ? 'Password reset failed, please try again later' : '密码重置失败，请稍后再试'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="card p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{language === 'en' ? 'Invalid Link' : '验证链接无效'}</h2>
          <p className="text-gray-600 mb-6">
            {language === 'en' ? 'Your password reset link is invalid or has expired, please apply again.' : '您的密码重置链接无效或已过期，请重新申请。'}
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="btn btn-primary"
          >
            {language === 'en' ? 'Apply Again' : '重新申请'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="card p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-6">
          {language === 'en' ? 'Set New Password' : '设置新密码'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'en' ? 'New Password' : '新密码'} <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder={language === 'en' ? 'At least 6 characters' : '至少6位字符'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'en' ? 'Confirm Password' : '确认密码'} <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder={language === 'en' ? 'Please enter password again' : '请再次输入密码'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? (language === 'en' ? 'Setting...' : '设置中...') : (language === 'en' ? 'Set New Password' : '设置新密码')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;